import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { configModel, ms_Config, config } from 'src/models/config.model';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppConfig } from 'src/app/app.config';
import { Observable, throwError, of } from 'rxjs';
import { ConfigStore } from 'src/app/store/config/config.store';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  userId: string;
  private readonly configStore = inject(ConfigStore);
  
  // Convert signals to observables in injection context
  private readonly config$ = toObservable(this.configStore.config);
  private readonly isReady$ = toObservable(this.configStore.isReady);

  constructor(private http: HttpClient, private router: Router) {
    // Lazy initialization - config loads on first access
  }

  //Loads configuration with caching to prevent duplicate API calls
  private loadConfig(): Observable<configModel> {
    // If already initialized, return cached config
    if (this.configStore.isInitialized()) {
      return of(this.configStore.config()!);
    }
    
    // If already loading, wait for it to complete
    if (this.configStore.isLoading()) {
      return this.config$.pipe(
        map(config => config!)
      );
    }
    
    this.configStore.setLoading(true);
    
    return this.http.get<any>('api/Config').pipe(
      map(resp => {
        const model = new configModel();
        model.config = resp.config;
        return model;
      }),
      tap(config => {
        this.configStore.setConfig(config);
        this.configStore.setLoading(false);
      }),
      catchError(err => {
        console.error('Failed to load configuration', err);
        this.configStore.setError(err.message || 'Failed to load configuration');
        return throwError(() => err);
      })
    );
  }

  //Reloads configuration by clearing cache and fetching fresh data
  reloadConfig(): Observable<configModel> {
    this.configStore.reset();
    return this.loadConfig();
  }

  // Returns observable that emits when config is loaded
  isConfigLoaded(): Observable<boolean> {
    // Trigger load if not initialized
    if (!this.configStore.isInitialized() && !this.configStore.isLoading()) {
      this.loadConfig().subscribe();
    }
    return this.isReady$;
  }

  // Returns current config value synchronously
  getConfigModel(): configModel | null {
    // Trigger load if not initialized
    if (!this.configStore.isInitialized() && !this.configStore.isLoading()) {
      this.loadConfig().subscribe();
    }
    return this.configStore.config();
  }

  
  // Returns observable stream of config changes
  getConfig$(): Observable<configModel | null> {
    // Trigger load if not initialized
    if (!this.configStore.isInitialized() && !this.configStore.isLoading()) {
      this.loadConfig().subscribe();
    }
    return this.config$;
  }

  //Gets contact us URL
  getContactUsUrl(): Observable<string> {
    return this.http.get('api/config/contactusurl', { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  // Gets app config by key
  getAppConfigByKey(key: string): Observable<ms_Config> {
    // If config is already loaded, return immediately
    if (this.configStore.isInitialized()) {
      const configValue = this.configStore.getConfigByKey(key);
      if (configValue) {
        return of(configValue);
      }
      return throwError(() => new Error(`Config key '${key}' not found`));
    }
    
    // Load config first, then retrieve the key
    return this.loadConfig().pipe(
      map(() => {
        const configValue = this.configStore.getConfigByKey(key);
        if (!configValue) {
          throw new Error(`Config key '${key}' not found`);
        }
        return configValue;
      }),
      catchError(err => throwError(() => err))
    );
  }

  //Gets valid user from MVC
  getMVCValidUser(): Observable<string> {
    return this.http.get(`${AppConfig.appsettings.navigatorRoot}Home/GetUser`, { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  // Updates about us configuration
  updateAboutUsConfig(configData: { Key: string, Value: string }[]): Observable<number> {
    return this.http.put<number>(`${AppConfig.appsettings.apiRoot}/UpdateConfig`, configData)
      .pipe(
        tap(() => this.reloadConfig()), // Reload config after update
        catchError(this.handleError)
      );
  }

  // Uploads site image with progress tracking
  uploadImage(formData: FormData): Observable<any> {
    return this.http.post(`${AppConfig.appsettings.apiRoot}/UploadSiteImage`, formData, {
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    }).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
            return { status: 'progress', message: progress };
          case HttpEventType.Response:
            return { status: 'completed', message: event.body };
          default:
            return { status: 'unknown', message: event };
        }
      }),
      catchError(this.handleError)
    );
  }

  // Gets current site image
  getCurrentImage(): Observable<{ imageUrl: string }> {
    return this.http.get<{ imageUrl: string }>(`${AppConfig.appsettings.apiRoot}/GetSiteImage`)
      .pipe(catchError(this.handleError));
  }

  // Centralized error handler
  private handleError(error: any): Observable<never> {
    console.error('API Error:', error.message || error);
    return throwError(() => new Error(error.message || 'Server error'));
  }
}
